import { UI } from '../../utils/arena/uiSounds';
import { ACCENT, BEVELED_BTN, TEXT_BODY } from '../../lib/medievalTheme';

/**
 * Back / Close button primitives. Two shared components so every
 * "go back one screen" button and every "dismiss this modal" button
 * reads identically across the app. A left-arrow glyph for
 * navigation, an X glyph for modal dismissal.
 *
 * Both accept an optional `title` for the native-tooltip hover
 * label (typically the previous screen's name. "Back to Hub",
 * "Back to Store" etc.) so we don't lose the contextual hint when
 * the text label goes away.
 */

const BUTTON_BASE_CLASS =
  'inline-flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-[1.05] active:scale-[0.95]';

const ICON_BUTTON_STYLE = {
  ...BEVELED_BTN,
  width: '2.125cqw',
  height: '2.125cqw',
  borderRadius: 'var(--radius-lg)',
  color: TEXT_BODY,
};

// The ghost close: no plate, only a wash of accent under the pointer and a
// deeper one while pressed. For panels whose header already frames the glyph.
const GHOST_ICON_BUTTON_STYLE = {
  width: ICON_BUTTON_STYLE.width,
  height: ICON_BUTTON_STYLE.height,
  borderRadius: 'var(--radius-lg)',
  color: TEXT_BODY,
  background: 'transparent',
};
const GHOST_HOVER_BG = `${ACCENT} 0.12)`;
const GHOST_ACTIVE_BG = `${ACCENT} 0.24)`;

// Numeric `size` is treated as design-pixels and rendered as cqw on the
// 1600 design canvas (1cqw = 16px) so the glyph scales with the canvas.
// Pass a string (e.g. "16px") to opt into a fixed pixel size.
function iconSizeStyle(size) {
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  return { width: sz, height: sz };
}

function ArrowLeftIcon({ size = 16 }) {
  return (
    <svg
      style={iconSizeStyle(size)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function ArrowRightIcon({ size = 16 }) {
  return (
    <svg
      style={iconSizeStyle(size)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="M12 5l7 7-7 7" />
    </svg>
  );
}

function XIcon({ size = 14 }) {
  return (
    <svg
      style={iconSizeStyle(size)}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

/**
 * Universal back-to-previous-screen button. Arrow-only. Pass
 * `title="Back to X"` so screen readers and mouse-hover users still
 * know where "back" goes. onClick is required.
 */
export function BackButton({ onClick, title = 'Back', className = '', style = {}, size = 16, testId }) {
  return (
    <button
      type="button"
      data-sound={UI.CANCEL}
      data-testid={testId}
      title={title}
      aria-label={title}
      className={`${BUTTON_BASE_CLASS} ${className}`}
      style={{
        ...ICON_BUTTON_STYLE,
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.45)`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.3)`; }}
      onClick={onClick}
    >
      <ArrowLeftIcon size={size} />
    </button>
  );
}

/**
 * Universal modal dismiss button. X-only. Defaults to a slightly
 * smaller glyph than BackButton since it's usually pinned to a
 * corner of a dialog rather than living in a screen header.
 */
/**
 * Right-pointing counterpart to BackButton. Used when "dismiss" means
 * "push this panel off to the side" rather than "go back to a previous
 * screen." E.g. the ChatView's close-chat button, which collapses the
 * open thread back to its list on the right.
 */
export function ForwardButton({ onClick, title = 'Close', className = '', style = {}, size = 16, ghost = false }) {
  // Ghost: no plate and no rim until the pointer is on it. The wash and the
  // press live in .vh-ghost-btn so they transition named properties; the
  // beveled variant's `transition-all` plus its inline borderColor writes
  // cannot be transitioned or interrupted cleanly.
  const geometry = {
    width: ICON_BUTTON_STYLE.width,
    height: ICON_BUTTON_STYLE.height,
    borderRadius: ICON_BUTTON_STYLE.borderRadius,
  };
  return (
    <button
      type="button"
      data-sound={UI.CANCEL}
      title={title}
      aria-label={title}
      className={`inline-flex items-center justify-center cursor-pointer ${ghost ? 'vh-ghost-btn' : BUTTON_BASE_CLASS} ${className}`}
      style={ghost ? { ...geometry, ...style } : { ...ICON_BUTTON_STYLE, ...style }}
      onMouseEnter={ghost ? undefined : (e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.45)`; }}
      onMouseLeave={ghost ? undefined : (e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.3)`; }}
      onClick={onClick}
    >
      <ArrowRightIcon size={size} />
    </button>
  );
}

export function CloseButton({ onClick, title = 'Close', className = '', style = {}, size = 14, ghost = false }) {
  const pointer = ghost
    ? {
      onMouseEnter: (e) => { e.currentTarget.style.background = GHOST_HOVER_BG; },
      onMouseLeave: (e) => { e.currentTarget.style.background = 'transparent'; },
      onMouseDown: (e) => { e.currentTarget.style.background = GHOST_ACTIVE_BG; },
      onMouseUp: (e) => { e.currentTarget.style.background = GHOST_HOVER_BG; },
    }
    : {
      onMouseEnter: (e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.45)`; },
      onMouseLeave: (e) => { e.currentTarget.style.borderColor = `${ACCENT} 0.3)`; },
    };
  return (
    <button
      type="button"
      data-sound={UI.CANCEL}
      title={title}
      aria-label={title}
      className={`${BUTTON_BASE_CLASS} ${className}`}
      style={{
        ...(ghost ? GHOST_ICON_BUTTON_STYLE : ICON_BUTTON_STYLE),
        ...style,
      }}
      {...pointer}
      onClick={onClick}
    >
      <XIcon size={size} />
    </button>
  );
}
